import * as cookieManager from 'js-cookie';
import {
  createFilterStateHelper,
  createPlanStateHelper,
  createUserStateHelper,
  createTrackingStateHelper,
} from './state-marshaller-utils';
import PlanModel from '../models/plan';
import PlanFuelModel from '../models/plan-fuel';
import GreenOptionModel from '../models/green-option';

/**
 * Creates a new GreenOptionModel for every `greenOption` array item found in `ContextHub.store.origin.products`
 * @param {Array} greenOptions
 * @returns {GreenOptionModel}
 */
function createGreenOptionsModels(greenOptions = []) {
  return greenOptions.map((option) => {
    return GreenOptionModel.create({
      approxCost: '',
      greenAmount: option.percent,
      extraCost: {
        amount: '',
        unit: '',
      },
    });
  });
}

/**
 * Creates a new PlanFuelModel for every `products` array item found in `ContextHub.store.origin`
 * @param {Array} products
 * @returns {PlanFuelModel}
 */
function createFuelModels(products = []) {
  return products.map((product) => {
    return PlanFuelModel.create({
      name: '',
      productCode: product.sapProductId,
      campaignId: product.campaignId,
      productId: product.productId,
      title: product.productTitle,
      description: product.productDescription,
      isFromSolarCampaign: product.isSolarCampaign,
      solarFitText: product.solarFitText,
      termsAndConditions: product.productTermsConditions,
      additionalTerms: product.productExtraTerms,
      discount: product.productDiscount,
      discountText: product.productDiscountText,
      isCrossSellItem: product.isCrossSellItem,
      greenOptions: createGreenOptionsModels(product.greenOption),
      tariff: [],
      fixedRebate: product.fixedRebate,
    });
  });
}

/**
 * We keep this in the parent scope for privacy and accessibility
 */
let filterState;
let planState;
let userState;
let trackingState;

/**
 * `StateMarshaller` singleton manages state mutation/translation into keys/values ContextHub understands
 *
 * @param {object} filterStateObject
 * @param {object} planStateObject
 * @param {object} userStateObject
 * @return {     getFilters: getFilters,
                setFilters: setFilters,
                getSelectedPlan: getSelectedPlan,
                setSelectedPlan: setSelectedPlan,
                getUser: getUser,
                setUser: setUser,
                reset: reset }
 * @constructor
 */
class StateMarshaller {
  constructor(
    filterStateObject = null,
    planStateObject = null,
    userStateObject = null,
    trackingStateObject = null,
  ) {
    if (!StateMarshaller.instance) {
      filterState = filterStateObject;
      planState = planStateObject;
      userState = userStateObject;
      trackingState = trackingStateObject;
      StateMarshaller.instance = this;
    }
    return StateMarshaller.instance;
  }

  getUser() {
    return {
      ...userState,
    };
  }

  setUser(user = {}) {
    Object.keys(user).forEach((key) => {
      userState[key] = user[key];
    });
  }

  /**
   * Retrieves an initial compare plans filter model. Pulls required values from ContextHub if present
   *
   * @public
   * @returns {Object} - compare plans filter model
   */
  getFilters() {
    return {
      ...filterState,
    };
  }

  /**
   * Sets compare plans filter model to ContextHub
   *
   * @public
   * @param {object} filters - modified compare plans filter model from UI layer
   */
  setFilters(filters = {}) {
    Object.keys(filters).forEach((key) => {
      filterState[key] = filters[key];
    });
  }

  /**
     * Retrieves compare plans PlanModel for initial UI state if
     ContextHub `products` are found. Returns null otherwise.
     *
     * @public
     * @returns {PlanModel|null}
     */
  getSelectedPlan() {
    if (!planState.fuels || planState.fuels.length === 0) {
      return null;
    }

    return PlanModel.create({
      title: planState.title,
      bonusOffer: planState.bonusOffer,
      fuels: createFuelModels(planState.fuels),
    });
  }

  /**
   * Sets analytics tracking values to Context Hub
   * @param tracking
   */
  setTracking(tracking = {}) {
    Object.keys(tracking).forEach((key) => {
      trackingState[key] = tracking[key];
    });
  }

  /**
   * Sets compare plans selected plan model to ContextHub
   *
   * @public
   * @param {object} selectedPlan - modified selected plan model from UI layer
   */
  setSelectedPlan(selectedPlan = {}) {
    Object.keys(selectedPlan).forEach((key) => {
      planState[key] = selectedPlan[key];
    });
  }

  /**
   * Resets singleton. Ensures and app-wide state changes are annihilated. Mainly used for testing :)
   *
   * @public
   */
  reset() {
    delete StateMarshaller.instance;
    filterState = undefined;
    planState = undefined;
    userState = undefined;
    trackingState = undefined;
  }
}
/**
 * Ensures single instance across app
 */
const instance = (sessionPersistence = null) => {
  if (!sessionPersistence) {
    throw new ReferenceError('Missing required parameter `sessionPersistence`');
  }
  return new StateMarshaller(
    createFilterStateHelper(sessionPersistence),
    createPlanStateHelper(sessionPersistence),
    createUserStateHelper(sessionPersistence),
    createTrackingStateHelper(sessionPersistence),
  );
};

export default instance;
